import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from 'src/environments/environment';
import {
  CmrRequest,
  CmrRequestResponse,
  CmrRequestListResponse,
  CmrConfig,
  CmrConfigResponse,
  CmrConfigListResponse,
  CmrConfigType,
  CmrJiraAuditLogApiResponse,
} from '../models/cmr-request.model';

@Injectable({
  providedIn: 'root',
})
export class CmrService {
  private apiUrl = `${environment.glassApiUrl}/v2/cmr`;

  constructor(private http: HttpClient) {}

  getRequests(): Observable<CmrRequestListResponse> {
    return this.http.get<CmrRequestListResponse>(this.apiUrl);
  }

  getRequestById(id: string): Observable<CmrRequestResponse> {
    return this.http.get<CmrRequestResponse>(`${this.apiUrl}/${id}`);
  }

  createRequest(request: Partial<CmrRequest>): Observable<CmrRequestResponse> {
    return this.http.post<CmrRequestResponse>(this.apiUrl, request);
  }

  updateRequest(id: string, request: Partial<CmrRequest>): Observable<CmrRequestResponse> {
    return this.http.put<CmrRequestResponse>(`${this.apiUrl}/${id}`, request);
  }

  /** Submitter sends a draft into the approval queue (optional body = latest content). */
  submitRequest(id: string, request?: Partial<CmrRequest>): Observable<CmrRequestResponse> {
    return this.http.post<CmrRequestResponse>(`${this.apiUrl}/${id}/submit`, request ?? {});
  }

  deleteRequest(id: string): Observable<CmrRequestResponse> {
    return this.http.delete<CmrRequestResponse>(`${this.apiUrl}/${id}`);
  }

  approveRequest(id: string): Observable<CmrRequestResponse> {
    return this.http.post<CmrRequestResponse>(`${this.apiUrl}/${id}/approve`, {});
  }

  rejectRequest(id: string, reason?: string): Observable<CmrRequestResponse> {
    return this.http.post<CmrRequestResponse>(`${this.apiUrl}/${id}/reject`, { reason: reason ?? '' });
  }

  /** Original submitter only; server enforces. */
  resubmitRequest(id: string): Observable<CmrRequestResponse> {
    return this.http.post<CmrRequestResponse>(`${this.apiUrl}/${id}/resubmit`, {});
  }

  /**
   * Pulls the latest Jira issue status into the CMR (used to reach **Completed** when Jira is done).
   */
  syncJiraStatus(id: string): Observable<CmrRequestResponse> {
    return this.http.post<CmrRequestResponse>(`${this.apiUrl}/${id}/sync-jira-status`, {});
  }

  postSlackManualUpdate(id: string, target: 'submission' | 'dayof' | 'both', message: string): Observable<{ success: boolean; message?: string; posted?: unknown[] }> {
    return this.http.post<{ success: boolean; message?: string; posted?: unknown[] }>(
      `${this.apiUrl}/${id}/slack/update`,
      { target, message },
    );
  }
  // ── Config (lookup data for form dropdowns) ──────────────────────────────

  getConfigs(type?: CmrConfigType): Observable<CmrConfigListResponse> {
    const params = type ? `?type=${type}` : '';
    return this.http.get<CmrConfigListResponse>(`${this.apiUrl}/config${params}`);
  }

  createConfig(entry: Partial<CmrConfig>): Observable<CmrConfigResponse> {
    return this.http.post<CmrConfigResponse>(`${this.apiUrl}/config`, entry);
  }

  updateConfig(id: string, entry: Partial<CmrConfig>): Observable<CmrConfigResponse> {
    return this.http.put<CmrConfigResponse>(`${this.apiUrl}/config/${id}`, entry);
  }

  deleteConfig(id: string): Observable<CmrConfigResponse> {
    return this.http.delete<CmrConfigResponse>(`${this.apiUrl}/config/${id}`);
  }

  // ── Jira audit timeline (admin) ─────────────────────────────────────────

  /** After approve, Jira issue REST create failed — approved CMR must have no linked issue key. */
  adminRetryCreateJiraIssue(cmrId: string): Observable<{ success: boolean; message: string; result?: unknown }> {
    return this.http.post<{ success: boolean; message: string; result?: unknown }>(
      `${this.apiUrl}/admin-retry-create-jira-issue`,
      { cmrId },
    );
  }

  /** Removes a persisted Jira audit row from Glass (timeline only; does not change Jira). */
  deleteJiraAuditLog(logId: string): Observable<{ success: boolean; message: string }> {
    return this.http.delete<{ success: boolean; message: string }>(`${this.apiUrl}/jira-audit-log/${logId}`);
  }

  /** Jira issue create / status-sync audit (admin timeline). */
  getJiraAuditLogs(): Observable<CmrJiraAuditLogApiResponse> {
    return this.http.get<CmrJiraAuditLogApiResponse>(`${this.apiUrl}/jira-audit-log`);
  }
}
